import { useState, useEffect } from "react";
import { X, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { adsSpyService } from "../services/adsSpy.service";
import { useAdsSpyStore } from "../store/adsSpyStore";
import type { SpyBrand } from "../types/adsSpy.types";
import { BRAND_CATEGORIES } from "../types/adsSpy.types";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

interface BrandModalProps {
  open: boolean;
  onClose: () => void;
  brand?: SpyBrand | null;
}

export default function BrandModal({ open, onClose, brand }: BrandModalProps) {
  const { language } = useAuth();
  const lang = language as Language;
  const t = (key: string, fallback: string) => dictionary[key]?.[lang] ?? fallback;
  const { loadBrands } = useAdsSpyStore();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [category, setCategory] = useState("General");

  useEffect(() => {
    if (open) {
      if (brand) {
        setName(brand.name);
        setUrl(brand.ad_library_url);
        setCategory(brand.category);
      } else {
        setName("");
        setUrl("");
        setCategory("General");
      }
    }
  }, [open, brand]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (brand) {
        await adsSpyService.updateBrand(brand.id, {
          name: name.trim(),
          ad_library_url: url.trim(),
          category,
        });
        toast.success(t("brand-updated", "Brand updated"));
      } else {
        await adsSpyService.createBrand({
          name: name.trim(),
          ad_library_url: url.trim(),
          category,
        });
        toast.success(t("brand-added", "Brand added"));
      }
      await loadBrands();
      onClose();
    } catch {
      toast.error(t("error-saving-brand", "Error saving brand"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-[20px] bg-white shadow-xl p-7">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-base font-semibold text-gray-900">
            {brand ? t("edit-brand", "Edit Brand") : t("add-brand", "Add Brand")}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                {t("brand-name-label", "Brand name")}
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("brand-name-placeholder", "E.g.: Nike, AG1, Dr. Squatch...")}
                className="w-full text-sm px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#5050EA] transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                {t("meta-ad-library-url", "Meta Ad Library URL")}
              </label>
              <textarea
                required
                rows={3}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t("paste-ad-library-url", "Paste the Ad Library URL here...")}
                className="w-full text-xs font-mono px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#5050EA] transition-colors resize-none"
              />
              <p className="text-[11px] mt-1.5 text-gray-400">
                {t("go-to-ad-library-hint", "Go to facebook.com/ads/library, search the brand and copy that URL")}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                {t("category", "Category")}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full appearance-none text-sm font-medium px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#5050EA] transition-colors"
              >
                {BRAND_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3 mt-7">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-[13px] font-medium rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
            >
              {t("cancel", "Cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 text-[13px] font-semibold rounded-xl bg-[#5050EA] text-white hover:bg-[#4040D0] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("save", "Save")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
